import React, { useState } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ModalProgressBar } from "../../../../_metronic/_partials/controls";
import { changePatientPassword } from "../_redux/patientsCrud";

function PatientSecurity() {
  const [loading, setLoading] = useState(false);
  const patient = useSelector((state) => state.patients.patient, shallowEqual);

  const initialValues = {
    password: "",
    confirmPassword: "",
  };

  const Schema = Yup.object().shape({
    password: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("New password is required"),
    confirmPassword: Yup.string()
      .required("Password confirmation is required")
      .oneOf([Yup.ref("password")], "Passwords do not match"),
  });

  const getInputClasses = (fieldname) => {
    if (formik.touched[fieldname] && formik.errors[fieldname]) {
      return "is-invalid";
    }

    if (formik.touched[fieldname] && !formik.errors[fieldname]) {
      return "is-valid";
    }

    return "";
  };

  const formik = useFormik({
    initialValues,
    validationSchema: Schema,
    onSubmit: (values, { setStatus, setSubmitting, setValues }) => {
      if (!patient?.id) {
        setStatus("Unable to determine the patient's account.");
        setSubmitting(false);
        return;
      }
      setLoading(true);
      setStatus(null);
      // A patient is itself a user, so patient.id is the user id.
      changePatientPassword(patient.id, values.password)
        .then(() => {
          setLoading(false);
          setSubmitting(false);
          setStatus({ success: "Password updated successfully." });
          setValues({ password: "", confirmPassword: "" });
        })
        .catch(() => {
          setLoading(false);
          setSubmitting(false);
          setStatus("Could not update the password. Please try again.");
        });
    },
  });

  return (
    <form
      className="card card-custom card-stretch"
      onSubmit={formik.handleSubmit}
    >
      {loading && <ModalProgressBar />}

      {/* begin::Header */}
      <div className="card-header py-3">
        <div className="card-title align-items-start flex-column">
          <h3 className="card-label font-weight-bolder text-dark">Security</h3>
          <span className="text-muted font-weight-bold font-size-sm mt-1">
            Set a new password for this patient's account
          </span>
        </div>
        <div className="card-toolbar">
          <button
            type="submit"
            className="btn btn-success mr-2"
            disabled={formik.isSubmitting || !formik.isValid}
          >
            Change Password
          </button>
        </div>
      </div>
      {/* end::Header */}

      {/* begin::Form */}
      <div className="form">
        <div className="card-body">
          {formik.status && formik.status.success ? (
            <div className="mb-10 alert alert-custom alert-light-success alert-dismissible">
              <div className="alert-text font-weight-bold">
                {formik.status.success}
              </div>
            </div>
          ) : (
            formik.status && (
              <div className="mb-10 alert alert-custom alert-light-danger alert-dismissible">
                <div className="alert-text font-weight-bold">
                  {formik.status}
                </div>
              </div>
            )
          )}

          <div className="form-group row">
            <label className="col-xl-3 col-lg-3 col-form-label">
              New Password
            </label>
            <div className="col-lg-9 col-xl-6">
              <input
                type="password"
                placeholder="New Password"
                className={`form-control form-control-lg form-control-solid ${getInputClasses(
                  "password"
                )}`}
                name="password"
                {...formik.getFieldProps("password")}
              />
              {formik.touched.password && formik.errors.password ? (
                <div className="invalid-feedback display-block">
                  {formik.errors.password}
                </div>
              ) : null}
            </div>
          </div>

          <div className="form-group row">
            <label className="col-xl-3 col-lg-3 col-form-label">
              Confirm Password
            </label>
            <div className="col-lg-9 col-xl-6">
              <input
                type="password"
                placeholder="Confirm Password"
                className={`form-control form-control-lg form-control-solid ${getInputClasses(
                  "confirmPassword"
                )}`}
                name="confirmPassword"
                {...formik.getFieldProps("confirmPassword")}
              />
              {formik.touched.confirmPassword &&
              formik.errors.confirmPassword ? (
                <div className="invalid-feedback display-block">
                  {formik.errors.confirmPassword}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      {/* end::Form */}
    </form>
  );
}

export default PatientSecurity;
